'use client';

import { useId } from 'react';
import { Flag } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatKnockoutStart, type KnockoutStart } from '@/utils/tournament-schedule';
import { formatDayLabel, type KnockoutMode } from './scheduleBlockUtils';

const OPTIONS: { value: KnockoutMode; title: string; description: string }[] = [
  {
    value: 'LAST_DAY',
    title: 'Último día',
    description: 'Las llaves ocupan todo el último día del torneo.',
  },
  {
    value: 'FROM_SLOT',
    title: 'Desde un horario',
    description: 'Por ejemplo, desde el sábado a las 20:00.',
  },
  {
    value: 'WHOLE_BLOCK',
    title: 'Todo el bloque',
    description: 'Un bloque aparte, solo para las llaves.',
  },
  {
    value: 'GROUPS_ONLY',
    title: 'Solo grupos',
    description: 'Las llaves van en otro bloque.',
  },
];

export interface KnockoutStartPanelProps {
  mode: KnockoutMode;
  onModeChange: (mode: KnockoutMode) => void;
  /** The cut that will be saved, already resolved against the grid. */
  knockoutStart: KnockoutStart | null;
  onSlotChange: (slot: KnockoutStart) => void;
  /** Days of the block that have courts, in order, with their start times. */
  days: { date: string; startTimes: string[] }[];
  /** Day that takes the whole bracket when there is no cut; null with a single day. */
  finalDay: string | null;
}

export function KnockoutStartPanel({
  mode,
  onModeChange,
  knockoutStart,
  onSlotChange,
  days,
  finalDay,
}: KnockoutStartPanelProps) {
  const baseId = useId();

  const handleDayChange = (date: string) => {
    const startTimes = days.find((d) => d.date === date)?.startTimes ?? [];
    const keepTime = knockoutStart && startTimes.includes(knockoutStart.startTime);
    const startTime = keepTime ? knockoutStart.startTime : startTimes[0];
    if (startTime) onSlotChange({ date, startTime });
  };

  const startTimesOfCut = days.find((d) => d.date === knockoutStart?.date)?.startTimes ?? [];

  return (
    <div className="space-y-3 rounded-lg border-2 border-amber-300 bg-amber-50/60 p-4">
      <div className="flex items-center gap-2 text-amber-900">
        <Flag className="h-4 w-4" />
        <h4 className="text-sm font-semibold">¿Desde cuándo se juegan las llaves?</h4>
      </div>

      <RadioGroup
        value={mode}
        onValueChange={(v) => onModeChange(v as KnockoutMode)}
        className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4"
      >
        {OPTIONS.map((opt) => {
          const id = `${baseId}-${opt.value}`;
          const isSelected = mode === opt.value;
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className={cn(
                'flex cursor-pointer items-start gap-2 rounded-md border bg-background p-3 transition-colors',
                isSelected ? 'border-amber-500 ring-1 ring-amber-500' : 'hover:border-amber-300',
              )}
            >
              <RadioGroupItem
                id={id}
                value={opt.value}
                className="mt-0.5 border-amber-600 text-amber-600"
              />
              <span className="space-y-0.5">
                <span className="block text-sm font-medium">{opt.title}</span>
                <span className="block text-xs text-muted-foreground">{opt.description}</span>
              </span>
            </label>
          );
        })}
      </RadioGroup>

      {mode === 'FROM_SLOT' && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>Arrancan el</span>
            <Select
              value={knockoutStart?.date ?? ''}
              onValueChange={handleDayChange}
              disabled={days.length === 0}
            >
              <SelectTrigger className="h-8 w-[9rem] bg-background capitalize" aria-label="Día">
                <SelectValue placeholder="Día" />
              </SelectTrigger>
              <SelectContent>
                {days.map((d) => (
                  <SelectItem key={d.date} value={d.date} className="capitalize">
                    {formatDayLabel(d.date)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span>a las</span>
            <Select
              value={knockoutStart?.startTime ?? ''}
              onValueChange={(startTime) =>
                knockoutStart && onSlotChange({ date: knockoutStart.date, startTime })
              }
              disabled={!knockoutStart}
            >
              <SelectTrigger className="h-8 w-[6.5rem] bg-background" aria-label="Hora">
                <SelectValue placeholder="Hora" />
              </SelectTrigger>
              <SelectContent>
                {startTimesOfCut.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-amber-900/80">
            También podés tocar la bandera
            <Flag className="mx-1 inline h-3 w-3 align-[-2px]" />
            de un horario en la grilla.
          </p>
        </div>
      )}

      <p className="text-sm font-medium text-amber-900">
        {knockoutSummary(mode, knockoutStart, days.length > 0, finalDay)}
      </p>
    </div>
  );
}

function knockoutSummary(
  mode: KnockoutMode,
  knockoutStart: KnockoutStart | null,
  hasCourts: boolean,
  finalDay: string | null,
): string {
  if (mode === 'GROUPS_ONLY') {
    return 'Este bloque queda para los grupos. Las llaves van en otro bloque del torneo, por ejemplo uno con «Todo el bloque».';
  }
  if (knockoutStart) {
    return mode === 'WHOLE_BLOCK'
      ? 'Todos los horarios de este bloque son de llaves.'
      : `Las llaves arrancan el ${formatKnockoutStart(knockoutStart, true)} y siguen hasta el final.`;
  }
  if (!hasCourts) return 'Cargá canchas en la grilla para ver dónde quedan las llaves.';
  if (mode === 'FROM_SLOT') {
    return finalDay
      ? `Elegí el horario. Mientras tanto, las llaves ocupan todo el último día (${formatDayLabel(finalDay)}).`
      : 'Elegí el horario desde el que arrancan las llaves.';
  }
  return finalDay
    ? `Las llaves ocupan todo el último día (${formatDayLabel(finalDay)}).`
    : 'Con un solo día, grupos y llaves se juegan ese mismo día.';
}
